import React, { useEffect, useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { styled } from '@ff/ui';
import IconButton from '@ff/ui/IconButton';
import Link from '@ff/ui/Link';
import Box from '@ff/ui/Box';
import Icon from '@ff/ui/Icon';
import Text from '@ff/ui/Text';
import FadeIn from '@ff/ui/Animation/FadeIn';
import { ZIndexBuilder } from '@ff/ui/utils/ZIndexBuilder';
import { isAndroid, isMobile, isWeChatBrowser } from 'shared/lib/useragentdetection';
import type { Props as HighlightsDetailProps } from './highlightsDetailProps';
import { getHandlerByKey } from 'shared/lib/appHandlers';
import { IS_APP } from 'shared/lib/pathUtils';
import setSafeAreaInsetTop from 'shared/lib/setSafeAreaInsetTop';

const QRCode = React.lazy(() => import(/* webpackChunkName: "qrcode" */ 'qrcode.react'));

const zIndices = new ZIndexBuilder({
  qrCode: {},
}).zIndices;

interface Props {
  isRtlLanguage: boolean;
  isShareDrawerOpen: boolean;
  zIndices?: {
    shareIconButton: number;
    shareDrawer: number;
  };
  onClose: () => void;
  setIsShareDrawerOpen: (value: boolean) => void;
}

export default function ShareDrawer(props: Props & Pick<HighlightsDetailProps, 'socialLinks'>) {
  const { isRtlLanguage, socialLinks, isShareDrawerOpen, zIndices, onClose, setIsShareDrawerOpen } = props;
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [showQRCode, setShowQRCode] = useState('');

  const showDesktopShareLinks = socialLinks && socialLinks.desktopDeviceShareLink?.length > 0;
  const showMobileShareLinks = socialLinks && socialLinks.mobileDeviceShareLink.url;

  useEffect(() => {
    function handleClickOutside(event) {
      // Handle escape key press
      if (event.key === 'Escape') {
        return setIsShareDrawerOpen(false);
      }
      // Handle outside mouse click
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        return setIsShareDrawerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleClickOutside)
    };
  }, [setIsShareDrawerOpen, sidebarRef]);

  useEffect(() => {
    if (isShareDrawerOpen && isMobile()) {
      const handleMobileNativeShare = async () => {

        const wechatBrowser = isWeChatBrowser();
        const android = isAndroid();

        // Mobile web browser share
        if (wechatBrowser && android) {
          const isCN = window.location.pathname.startsWith('/cn') || window.location.hostname.includes('faradayfuturecn');
          if (isCN) {
            alert('点击右上角"..."去分享');
          } else {
            alert('Click the 3 dots in the upper right corner to share');
          }
        } else if (navigator.share) {
          try {
            const shareDetails = {
              url: socialLinks?.mobileDeviceShareLink?.url,
              title: socialLinks?.mobileDeviceShareLink?.title,
              text: socialLinks?.mobileDeviceShareLink?.text
            };
            await navigator.share(shareDetails);
          } catch (error) {
            if (error.toString().includes('AbortError')) {
              // Add callback if the user decides to close the share popup
              setIsShareDrawerOpen(false);
            }
          }
        } else {
          // fallback code
          console.error('Web share is currently not supported on this browser.');
        }
        // Close after the user clicks to share the page
        setIsShareDrawerOpen(false);
      };
      handleMobileNativeShare();
    }
  }, [isShareDrawerOpen, setIsShareDrawerOpen, socialLinks?.mobileDeviceShareLink?.text, socialLinks?.mobileDeviceShareLink?.title, socialLinks?.mobileDeviceShareLink?.url]);

  const handleOpenEmailShareLink = (element) => {
    const subject = element.emailSubject;
    const body = element.emailBody;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  }

  return (
    <>
      {socialLinks && (
        <ShareIconButton
          isRtlLanguage={isRtlLanguage}
          showDesktopShareLinks={!!showDesktopShareLinks}
          showMobileShareLinks={!!showMobileShareLinks}
          zIndex={zIndices.shareIconButton}
          iconProps={{ fontSize: '17px' }}
          icon='ff-icon-share_2'
          onClick={() => {
            if (IS_APP) {
              const wechatBrowser = isWeChatBrowser();
              
              // Special handling for WeChat browser
              if (wechatBrowser) {
                const isCN = window.location.pathname.startsWith('/cn') || window.location.hostname.includes('faradayfuturecn');
                if (isCN) {
                  alert('点击右上角"..."去分享');
                } else {
                  alert('Click the 3 dots in the upper right corner to share');
                }
                return;
              }
              
              // Regular app share handler
              const handler = getHandlerByKey('Share', {
                url: socialLinks.mobileDeviceShareLink.url,
                title: socialLinks.mobileDeviceShareLink.title,
                description: socialLinks.mobileDeviceShareLink.text,
                image: socialLinks.mobileDeviceShareLink?.imageUrl,
              });
              handler();
            } else {
              setIsShareDrawerOpen(true);
            }
          }}
        />
      )}

      {/* 
        Mobile Share handled in the useEffect: 
        Opens the native share post popup.
      */}

      {/* Desktop Share */}
      {(isShareDrawerOpen && !isMobile()) && (
        <FocusLock disabled={!isShareDrawerOpen} autoFocus={true}>
          <FadeIn duration={0.3} persist>
            <DesktopShare zIndex={zIndices.shareDrawer}>
              <Sidebar ref={sidebarRef}>
                <IconContainer>
                  <CloseIconButton
                    zIndex={zIndices['closeDrawerIcon']}
                    icon='ff-icon-x-circle'
                    iconProps={{ fontSize: '30px' }}
                    onClick={onClose}
                  />
                </IconContainer>
                {socialLinks && socialLinks?.desktopDeviceShareLink?.map((element, i) => (
                  element?.icon?.includes('wechat') || element?.icon?.includes('wecom') ? (
                    <Box
                      tabEnter
                      onFocus={() => setShowQRCode(element.icon)}
                      onBlur={() => setShowQRCode('')}
                      onMouseEnter={() => setShowQRCode(element.icon)}
                      onMouseLeave={() => setShowQRCode('')}
                    >
                      <SocialLink
                        showQRCode={showQRCode}
                        url={element.url}
                        icon={element.icon}
                        text={element.text}
                      />
                    </Box>
                  ) : (
                    <Link 
                      key={i} 
                      href={element.url}
                      target='_blank' 
                      rel='noopener noreferrer'
                    >
                      <SocialLink 
                        url={element.url}
                        icon={element.icon}
                        text={element.text}
                      />
                    </Link>
                  )
                ))}
                {socialLinks && socialLinks?.desktopDeviceShareEmailLink?.map((element, i) => (
                  <Link 
                    key={i} 
                    href='#'
                    onClick={() => handleOpenEmailShareLink(element)}
                  >
                    <SocialLink 
                      icon={element.icon}
                      text={element.text}
                      url={element.url}
                    />
                  </Link>
                ))}
              </Sidebar>
            </DesktopShare>
          </FadeIn>
        </FocusLock>
      )}
    </>
  );
}

interface SocialLinkProps {
  showQRCode?: string;
  icon: string;
  text: string;
  url: string;
}

const SocialLink = ({ showQRCode, url, icon, text }: SocialLinkProps) => (
  <Item>
    <QRCodeContainer display={(showQRCode && showQRCode === icon) ? 'block' : 'none'}>
      {(showQRCode && url) && (
        <QRCode value={url} />
      )}
    </QRCodeContainer>
    <Box display='flex' justifyContent='space-between' columnGap='16px'>
      <Icon name={icon} fontSize='18px' />
      <P14>{text}</P14>
    </Box>
  </Item>
);

const QRCodeContainer = styled(Box)({
  position: 'absolute',
  top: '2.5em',
  zIndex: zIndices['qrCode'],
});

const ShareIconButton =
  styled(IconButton)<{ isRtlLanguage: boolean , zIndex: number, showMobileShareLinks: boolean, showDesktopShareLinks: boolean }>((
    { theme, isRtlLanguage, zIndex, showMobileShareLinks, showDesktopShareLinks }
  ) => ({
    alignItems: 'center',
    background: 'rgba(23, 26, 32, 0.5)',
    borderRadius: '50%',
    color: 'white',
    display: showMobileShareLinks ? 'flex' : 'none',
    height: '30px',
    justifyContent: 'center',
    padding: '6px',
    position: 'absolute',
    right: isRtlLanguage ? undefined : '20px',
    left: isRtlLanguage ? '20px' : undefined,
    top: IS_APP ? setSafeAreaInsetTop() : '1.1em',
    transition: 'background 0.3s ease, color 0.3s ease',
    width: '30px',
    zIndex: zIndex,
    '&:hover': {
      background: 'rgb(105, 106, 110)',
      color: 'white',
    },
    [theme.mediaQueries.md]: {
      background: 'rgba(212, 212, 212, 1)',
      color: 'rgba(60, 60, 67, 0.8)',
      display: showDesktopShareLinks ? 'block' : 'none',
      right: isRtlLanguage ? undefined : '32px',
      left: isRtlLanguage ? '32px' : undefined,
      top: '50px',
    }
  }));

const IconContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'end',
  marginBottom: '50px',
  width: '100%',
});

const CloseIconButton = styled(IconButton)<{ zIndex: number }>(({ zIndex }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  transitionDuration: '200ms',
  transitionProperty: 'color ease',
  zIndex: zIndex,
  '&:hover': {
    color: 'rgba(105, 106, 110, 1)',
  },
}));

const Item = styled(Box)({
  alignItems: 'center',
  borderRadius: '1px',
  display: 'flex',
  color: 'rgba(255, 255, 255, 0.7)',
  cursor: 'pointer',
  gap: '10px',
  marginBottom: '6px',
  paddingTop: '9px',
  paddingBottom: '9px',
  paddingLeft: '12px',
  paddingRight: '12px',
  position: 'relative',
  transitionDuration: '200ms',
  transitionProperty: 'background ease, color ease',
  '&:hover': {
    background: 'rgba(116, 116, 128, 0.3)',
    color: 'white',
  },
});

const DesktopShare = styled(Box)({
  backdropFilter: 'blur(4px)',
  background: 'rgba(23, 26, 32, 0.6)',
  display: 'flex',
  justifyContent: 'flex-end',
  height: '100%',
  position: 'absolute',
  top: 0,
  width: '100%',
});

const Sidebar = styled(Box)({
  background: 'rgba(12, 13, 15, 0.9)',
  color: 'white',
  height: '100%',
  paddingBottom: '50px',
  paddingLeft: '32px',
  paddingRight: '32px',
  paddingTop: '50px',
  width: '311px',
});

const P14 = styled(Text)({
  fontSize: '14px',
});
